import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import toast from "react-hot-toast";
import { EVENT_TAXONOMY_NAME_MAX_LENGTH, EventSubType } from "../../models/events";
import { yupConstants } from "../../utils/constants/yup";
import { getApiErrorMessage } from "../../utils/functions/api-errors";
import { useTenantScope } from "../../hooks/useTenantScope";
import {
  useGetEventTypesQuery,
  useNewEventSubTypeMutation,
  useUpdateEventSubTypeMutation,
} from "../../store/services/events-types";

interface SubTypeFormProps {
  subTypeData?: EventSubType;
  handleClose: () => void;
}

interface SubTypeFormValues {
  eventSubTypeName: string;
  eventTypeId: number | null;
}

export const SubTypeForm = ({ subTypeData, handleClose }: SubTypeFormProps) => {
  const scope = useTenantScope();
  const tenantId = scope.tenantId;
  const { data: eventTypes, isLoading: isLoadingTypes } = useGetEventTypesQuery(undefined, {
    skip: scope.loading,
  });
  const [newEventSubType, { isLoading: isCreating }] = useNewEventSubTypeMutation();
  const [updateEventSubType, { isLoading: isUpdating }] = useUpdateEventSubTypeMutation();
  const isSaving = isCreating || isUpdating;
  const isEdit = !!subTypeData;

  const { handleSubmit, touched, errors, values, handleBlur, handleChange, setFieldValue } =
    useFormik<SubTypeFormValues>({
      initialValues: {
        eventSubTypeName: subTypeData?.eventSubTypeName || "",
        eventTypeId: subTypeData?.eventTypeId ?? null,
      },
      validationSchema: Yup.object({
        eventSubTypeName: Yup.string()
          .trim()
          .required(yupConstants().required)
          .max(EVENT_TAXONOMY_NAME_MAX_LENGTH, yupConstants(EVENT_TAXONOMY_NAME_MAX_LENGTH).max),
        eventTypeId: Yup.number()
          .typeError(yupConstants().required)
          .required(yupConstants().required),
      }),
      onSubmit: ({ eventTypeId, eventSubTypeName }) => {
        if (eventTypeId === null) {
          return;
        }

        const trimmedName = eventSubTypeName.trim();

        if (subTypeData) {
          updateEventSubType({
            eventSubTypeId: subTypeData.eventSubTypeId,
            eventTypeId,
            eventSubTypeName: trimmedName,
            tenantId: tenantId ?? null,
          })
            .unwrap()
            .then(() => {
              toast.success("Subtipo actualizado");
              handleClose();
            })
            .catch((error) => {
              toast.error(getApiErrorMessage(error) || "No se pudo actualizar el subtipo");
            });
          return;
        }

        newEventSubType({
          eventTypeId,
          eventSubTypeName: trimmedName,
          tenantId: tenantId ?? null,
        })
          .unwrap()
          .then(() => {
            toast.success("Subtipo creado");
            handleClose();
          })
          .catch((error) => {
            toast.error(getApiErrorMessage(error) || "No se pudo crear el subtipo");
          });
      },
    });

  return (
    <form className="flex flex-column" style={{ gap: 18 }} onSubmit={handleSubmit}>
      <div className="flex flex-column" style={{ gap: 6 }}>
        <label htmlFor="eventTypeId" className="font-medium">
          Tipo
        </label>
        <Dropdown
          inputId="eventTypeId"
          name="eventTypeId"
          className="w-full"
          appendTo="self"
          value={values.eventTypeId}
          options={eventTypes}
          optionLabel="eventTypeName"
          optionValue="eventTypeId"
          placeholder="Seleccionar tipo"
          loading={isLoadingTypes}
          disabled={isSaving}
          invalid={!!touched.eventTypeId && !!errors.eventTypeId}
          onBlur={handleBlur}
          onChange={(event) => setFieldValue("eventTypeId", event.value ?? null)}
        />
        {touched.eventTypeId && errors.eventTypeId && (
          <small className="p-error">{errors.eventTypeId}</small>
        )}
      </div>

      <div className="flex flex-column" style={{ gap: 6 }}>
        <label htmlFor="eventSubTypeName" className="font-medium">
          Nombre
        </label>
        <InputText
          id="eventSubTypeName"
          name="eventSubTypeName"
          className="w-full"
          value={values.eventSubTypeName}
          onChange={handleChange}
          onBlur={handleBlur}
          invalid={!!touched.eventSubTypeName && !!errors.eventSubTypeName}
          disabled={isSaving}
        />
        {touched.eventSubTypeName && errors.eventSubTypeName && (
          <small className="p-error">{errors.eventSubTypeName}</small>
        )}
      </div>

      <div className="form-actions flex flex-wrap justify-content-end pt-2" style={{ gap: "var(--space-18)" }}>
        <Button
          type="submit"
          label={isEdit ? "Guardar cambios" : "Crear subtipo"}
          loading={isSaving}
        />
        <Button
          type="button"
          label="Cancelar"
          severity="secondary"
          outlined
          onClick={handleClose}
          disabled={isSaving}
        />
      </div>
    </form>
  );
};
